// pages/VoiceLibraryPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, Search, Star, Mic, Loader, X } from "lucide-react";
import api from "../../services/api";

// ── Tier badge config (mirrors VoiceSelector) ─────────────────────────────────
const TIER_BADGE = {
  chirp3: {
    label: "Chirp 3 HD",
    bg: "rgba(139,92,246,0.15)",
    color: "#c4b5fd",
    border: "rgba(139,92,246,0.3)",
  },
  chirp: {
    label: "Chirp HD",
    bg: "rgba(59,130,246,0.15)",
    color: "#93c5fd",
    border: "rgba(59,130,246,0.3)",
  },
  studio: {
    label: "Studio",
    bg: "rgba(245,158,11,0.15)",
    color: "#fcd34d",
    border: "rgba(245,158,11,0.3)",
  },
  neural2: {
    label: "Neural2",
    bg: "rgba(16,185,129,0.15)",
    color: "#6ee7b7",
    border: "rgba(16,185,129,0.3)",
  },
  news: {
    label: "News",
    bg: "rgba(14,165,233,0.15)",
    color: "#7dd3fc",
    border: "rgba(14,165,233,0.3)",
  },
  wavenet: {
    label: "WaveNet",
    bg: "rgba(20,184,166,0.15)",
    color: "#5eead4",
    border: "rgba(20,184,166,0.3)",
  },
  standard: {
    label: "Standard",
    bg: "rgba(107,114,128,0.15)",
    color: "#9ca3af",
    border: "rgba(107,114,128,0.3)",
  },
};

const AVATAR_GRADIENTS = [
  "from-violet-500 via-purple-500 to-fuchsia-500",
  "from-cyan-500 via-blue-500 to-indigo-500",
  "from-emerald-500 via-teal-500 to-cyan-500",
  "from-rose-500 via-pink-500 to-fuchsia-500",
  "from-amber-500 via-orange-500 to-rose-500",
  "from-lime-500 via-green-500 to-emerald-500",
];

const LANGUAGES = [
  { label: "All languages", code: "" },
  // English
  { label: "English (UK)", code: "en-GB" },
  { label: "English (US)", code: "en-US" },
  { label: "English (AU)", code: "en-AU" },
  { label: "English (IN)", code: "en-IN" },
  // Western European
  { label: "Spanish (Spain)", code: "es-ES" },
  { label: "Spanish (US)", code: "es-US" },
  { label: "French", code: "fr-FR" },
  { label: "French (CA)", code: "fr-CA" },
  { label: "German", code: "de-DE" },
  { label: "Italian", code: "it-IT" },
  { label: "Portuguese (BR)", code: "pt-BR" },
  { label: "Dutch", code: "nl-NL" },
  // Northern / Eastern European
  { label: "Swedish", code: "sv-SE" },
  { label: "Danish", code: "da-DK" },
  { label: "Finnish", code: "fi-FI" },
  { label: "Norwegian", code: "nb-NO" },
  { label: "Polish", code: "pl-PL" },
  { label: "Russian", code: "ru-RU" },
  { label: "Czech", code: "cs-CZ" },
  { label: "Romanian", code: "ro-RO" },
  { label: "Hungarian", code: "hu-HU" },
  { label: "Greek", code: "el-GR" },
  { label: "Hebrew", code: "he-IL" },
  // South / Southeast Asian
  { label: "Hindi", code: "hi-IN" },
  { label: "Indonesian", code: "id-ID" },
  { label: "Thai", code: "th-TH" },
  { label: "Vietnamese", code: "vi-VN" },
  // East Asian
  { label: "Japanese", code: "ja-JP" },
  { label: "Korean", code: "ko-KR" },
  // Middle East / Africa
  { label: "Arabic", code: "ar-XA" },
  { label: "Turkish", code: "tr-TR" },
  { label: "Swahili", code: "sw-KE" },
];

// ── Filter Chip ───────────────────────────────────────────────────────────────
const FilterChip = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
    style={{
      background: active ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.04)",
      border: active
        ? "1px solid rgba(139,92,246,0.5)"
        : "1px solid rgba(255,255,255,0.07)",
      color: active ? "#c4b5fd" : "#6b7280",
    }}
  >
    {label}
  </button>
);

// ── Voice Card ────────────────────────────────────────────────────────────────
const VoiceCard = ({ voice, index, playingId, onTogglePlay }) => {
  const isPlaying = playingId === voice.id;
  const tier = TIER_BADGE[voice.tier] || TIER_BADGE.standard;
  const gradient = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];

  return (
    <div
      className="group flex flex-col items-center p-5 rounded-2xl border transition-all duration-200 cursor-default"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(139,92,246,0.3)";
        e.currentTarget.style.background = "rgba(139,92,246,0.04)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
      }}
    >
      {/* Avatar */}
      <div className="relative mb-4">
        <div
          className={`w-20 h-20 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center overflow-hidden ring-2 ring-transparent group-hover:ring-violet-500/30 transition-all`}
        >
          {voice.avatarImageUrl ? (
            <>
              <img
                src={voice.avatarImageUrl}
                alt={voice.displayName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <span
                className="text-2xl font-bold text-white"
                style={{ display: "none" }}
              >
                {voice.displayName[0]}
              </span>
            </>
          ) : (
            <span className="text-2xl font-bold text-white">
              {voice.displayName[0]}
            </span>
          )}
        </div>

        {/* Premium star */}
        {voice.isPremium && (
          <div
            className="absolute -top-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center"
            style={{ background: "#d97706" }}
          >
            <Star size={10} fill="white" className="text-white" />
          </div>
        )}

        {/* Play button */}
        {voice.sampleAudioUrl && (
          <button
            onClick={() => onTogglePlay(voice)}
            className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg"
            style={{
              background: isPlaying ? "#6d28d9" : "#7c3aed",
              boxShadow: "0 4px 12px rgba(124,58,237,0.4)",
            }}
          >
            {isPlaying ? (
              <Pause size={12} fill="white" className="text-white" />
            ) : (
              <Play size={12} fill="white" className="text-white" />
            )}
          </button>
        )}
      </div>

      {/* Name */}
      <p className="font-semibold text-white text-sm text-center leading-tight mb-1">
        {voice.displayName}
      </p>

      {/* Gender + accent */}
      <p className="text-xs text-gray-500 capitalize mb-2.5">
        {voice.gender} · {voice.accent || "British"}
      </p>

      {/* Tier badge */}
      <span
        className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full mb-2.5"
        style={{
          background: tier.bg,
          color: tier.color,
          border: `1px solid ${tier.border}`,
        }}
      >
        {tier.label}
      </span>

      {/* Characteristics */}
      {voice.characteristics?.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center">
          {voice.characteristics.slice(0, 2).map((c) => (
            <span
              key={c}
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: "rgba(255,255,255,0.06)", color: "#6b7280" }}
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {/* Playing indicator */}
      {isPlaying && (
        <div className="flex items-center gap-[2px] mt-2">
          {[4, 7, 5, 9, 6, 8, 4].map((h, i) => (
            <div
              key={i}
              className="w-[2px] rounded-full animate-pulse bg-violet-400"
              style={{ height: `${h}px`, animationDelay: `${i * 80}ms` }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const VoiceLibraryPage = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [langCode, setLangCode] = useState("");
  const [gender, setGender] = useState("");
  const [tier, setTier] = useState("");
  const [isPremium, setIsPremium] = useState("");
  const [search, setSearch] = useState("");

  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    loadVoices(controller.signal);
    return () => controller.abort();
  }, [langCode, gender, tier, isPremium, search]);

  const loadVoices = async (signal) => {
    setLoading(true);
    try {
      const params = { languageCode: langCode, limit: 200 };
      if (gender) params.gender = gender;
      if (tier) params.tier = tier;
      if (isPremium) params.isPremium = isPremium;
      if (search) params.search = search;
      const response = await api.getVoices(params.languageCode, params);
      setVoices(response.avatars || []);
      setTotal(response.total || 0);
    } catch (err) {
      if (err.name !== "AbortError")
        console.error("Failed to load voices:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlay = (voice) => {
    if (!voice.sampleAudioUrl) return;
    if (playingId === voice.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(voice.sampleAudioUrl);
    audioRef.current = audio;
    audio.play().catch(console.error);
    audio.onended = () => setPlayingId(null);
    setPlayingId(voice.id);
  };

  useEffect(
    () => () => {
      audioRef.current?.pause();
    },
    [],
  );

  const resetFilters = () => {
    setGender("");
    setTier("");
    setIsPremium("");
    setSearch("");
  };
  const hasFilters = gender || tier || isPremium || search;

  const tiers = [
    "chirp3",
    "chirp",
    "studio",
    "neural2",
    "news",
    "wavenet",
    "standard",
  ];

  return (
    <div
      className="flex flex-col h-full min-h-screen text-white"
      style={{ background: "#070a10" }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-8 py-5 flex-shrink-0"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(7,10,16,0.9)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #7c3aed, #6d28d9)" }}
          >
            <Mic size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">
              Voice Library
            </h1>
            <p className="text-xs text-gray-600 mt-0.5">
              {loading
                ? "Loading…"
                : `${total} voice${total !== 1 ? "s" : ""} available`}
            </p>
          </div>
        </div>
      </div>

      {/* ── Filters bar ── */}
      <div
        className="flex flex-wrap items-center gap-2.5 px-8 py-4 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      >
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
            size={13}
          />
          <input
            type="text"
            placeholder="Search voices…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl text-sm text-white placeholder-gray-700 focus:outline-none transition-all"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = "rgba(139,92,246,0.4)")
            }
            onBlur={(e) =>
              (e.target.style.borderColor = "rgba(255,255,255,0.07)")
            }
          />
        </div>

        {/* Language */}
        <select
          value={langCode}
          onChange={(e) => setLangCode(e.target.value)}
          className="px-3 py-2 rounded-xl text-sm text-white focus:outline-none transition-all"
          style={{
            background: "#0d1018",
            border: "1px solid rgba(255,255,255,0.07)",
            colorScheme: "dark",
          }}
        >
          {LANGUAGES.map((l) => (
            <option
              key={l.code}
              value={l.code}
              style={{ background: "#0d1018", color: "#fff" }}
            >
              {l.label}
            </option>
          ))}
        </select>

        {/* Gender */}
        <select
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          className="px-3 py-2 rounded-xl text-sm text-white focus:outline-none"
          style={{
            background: "#0d1018",
            border: "1px solid rgba(255,255,255,0.07)",
            colorScheme: "dark",
          }}
        >
          <option value="" style={{ background: "#0d1018", color: "#fff" }}>
            All genders
          </option>
          <option value="male" style={{ background: "#0d1018", color: "#fff" }}>
            Male
          </option>
          <option
            value="female"
            style={{ background: "#0d1018", color: "#fff" }}
          >
            Female
          </option>
        </select>

        {/* Tier */}
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="px-3 py-2 rounded-xl text-sm text-white focus:outline-none"
          style={{
            background: "#0d1018",
            border: "1px solid rgba(255,255,255,0.07)",
            colorScheme: "dark",
          }}
        >
          <option value="" style={{ background: "#0d1018", color: "#fff" }}>
            All tiers
          </option>
          {Object.entries(TIER_BADGE).map(([key, val]) => (
            <option
              key={key}
              value={key}
              style={{ background: "#0d1018", color: "#fff" }}
            >
              {val.label}
            </option>
          ))}
        </select>

        {/* Premium */}
        <select
          value={isPremium}
          onChange={(e) => setIsPremium(e.target.value)}
          className="px-3 py-2 rounded-xl text-sm text-white focus:outline-none"
          style={{
            background: "#0d1018",
            border: "1px solid rgba(255,255,255,0.07)",
            colorScheme: "dark",
          }}
        >
          <option value="" style={{ background: "#0d1018", color: "#fff" }}>
            All voices
          </option>
          <option value="true" style={{ background: "#0d1018", color: "#fff" }}>
            Premium only
          </option>
          <option
            value="false"
            style={{ background: "#0d1018", color: "#fff" }}
          >
            Standard only
          </option>
        </select>

        {hasFilters && (
          <button
            onClick={resetFilters}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-violet-400 hover:text-violet-300 transition-colors"
            style={{
              background: "rgba(139,92,246,0.08)",
              border: "1px solid rgba(139,92,246,0.2)",
            }}
          >
            <X size={11} /> Reset
          </button>
        )}
      </div>

      {/* ── Grid ── */}
      <div
        className="flex-1 overflow-y-auto px-8 py-6"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(139,92,246,0.2) transparent",
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-64 gap-3">
            <div
              className="w-6 h-6 rounded-full animate-spin"
              style={{
                border: "2px solid rgba(139,92,246,0.2)",
                borderTopColor: "#7c3aed",
              }}
            />
            <span className="text-sm text-gray-600">Loading voices…</span>
          </div>
        ) : voices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Mic size={28} className="text-gray-700" />
            <p className="text-sm text-gray-500">
              No voices match your filters
            </p>
            <button
              onClick={resetFilters}
              className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {voices.map((voice, i) => (
              <VoiceCard
                key={voice.id}
                voice={voice}
                index={i}
                playingId={playingId}
                onTogglePlay={handleTogglePlay}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Stats bar ── */}
      {!loading && voices.length > 0 && (
        <div
          className="flex items-center gap-6 px-8 py-3 flex-shrink-0"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <span className="text-sm font-bold text-violet-400">
            {voices.length}
          </span>
          <span className="text-[11px] text-gray-700">voices shown</span>
          {playingId && (
            <div className="flex items-center gap-2 ml-auto">
              <div className="flex items-center gap-[2px]">
                {[4, 7, 5, 9, 6].map((h, i) => (
                  <div
                    key={i}
                    className="w-[2px] rounded-full animate-pulse bg-violet-400"
                    style={{ height: `${h}px`, animationDelay: `${i * 80}ms` }}
                  />
                ))}
              </div>
              <span className="text-[11px] text-gray-600">Playing sample</span>
              <button
                onClick={() => {
                  audioRef.current?.pause();
                  setPlayingId(null);
                }}
                className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors"
              >
                Stop
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceLibraryPage;
